import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import type { Situacao } from '@/features/cotacoes/situacao';

/**
 * O que a página inicial lê de cada módulo. Cada resumo é uma chamada à parte,
 * feita só por quem tem o módulo e a permissão: com o banco remoto, um bloco
 * não espera o outro.
 */

export type ResumoCompras = {
  abertas: { id: number; numero: number; descricao: string; situacao: Situacao }[];
  periodo: { inicio: string; fim: string };
  totais: {
    fechamentos: number;
    valor_fechado: number;
    pacote_inicial: number;
    pacote_fechado: number;
    ganho: number;
    ganho_pct: number;
  };
  por_mes: { mes: string; rotulo: string; fechamentos: number; valor_fechado: number; ganho: number }[];
  por_fornecedor: { apelido: string; logo_url: string | null; valor: number; fechamentos: number }[];
  ultimo: {
    cotacao_id: number;
    numero: number | null;
    fornecedor: string | null;
    data: string;
    valor_total: number;
    ganho: number;
    ganho_pct: number;
  } | null;
};

/** "acao" depende de quem usa o sistema; "espera", de fornecedor ou do escritório. */
export type Tom = 'acao' | 'espera';

export type PendenciaDaFolha = { titulo: string; detalhe: string; tom: Tom; destino: string; em: string | null };

export type StatusFolha = 'rascunho' | 'conferida' | 'exportada';

export type ResumoFolha = {
  atual: {
    id: number;
    rotulo: string;
    status: StatusFolha;
    importado_em: string | null;
    proventos: number;
    descontos: number;
    liquido: number;
    funcionarios: number;
    anterior: { rotulo: string; proventos: number; liquido: number; funcionarios: number } | null;
  } | null;
  serie: { rotulo: string; proventos: number; liquido: number; funcionarios: number }[];
  pendencias: PendenciaDaFolha[];
};

type ValoresMetais = {
  cobre_usd: number | null;
  aluminio_usd: number | null;
  dolar: number | null;
  cobre_brl: number | null;
  aluminio_brl: number | null;
};

export type VariacaoMetais = Record<keyof ValoresMetais, number | null> & { desde: string };

export type PainelMetais = {
  ultimo_pregao: string | null;
  capturado_em: string | null;
  desatualizado: boolean;
  hoje: (ValoresMetais & { data: string }) | null;
  variacoes: Record<'semana' | 'quinzena' | 'mes', VariacaoMetais | null> | null;
  periodos: (ValoresMetais & { rotulo: string; inicio: string })[];
};

/** Módulo contratado e a permissão de ver: as mesmas duas guardas da rota no backend. */
export function useAlcanca(modulo: string, permissao: string): boolean {
  return useAuthStore(
    (estado) =>
      (estado.user?.modulos_ativos ?? []).includes(modulo) && (estado.user?.permissoes ?? []).includes(permissao),
  );
}

export function useResumoCompras(habilitado: boolean) {
  return useQuery({
    queryKey: ['inicio', 'cotacao'],
    queryFn: async () => (await api.get<{ data: ResumoCompras }>('/inicio/cotacao')).data.data,
    enabled: habilitado,
  });
}

export function useResumoFolha(habilitado: boolean) {
  return useQuery({
    queryKey: ['inicio', 'folha'],
    queryFn: async () => (await api.get<{ data: ResumoFolha }>('/inicio/folha')).data.data,
    enabled: habilitado,
  });
}

/** A mesma chave do painel de índices no recorte diário: abrir o modal depois não busca de novo. */
export function usePainelMetais(habilitado: boolean) {
  return useQuery({
    queryKey: ['indices', 'diario', 1],
    queryFn: async () =>
      (await api.get<{ data: PainelMetais }>('/indices', { params: { agrupamento: 'diario', meses: 1 } })).data.data,
    enabled: habilitado,
  });
}
